import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Linking, Platform, Pressable } from "react-native";
import { MotiView } from "moti";
import WebView from "react-native-webview";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useRoute, useFocusEffect, RouteProp } from "@react-navigation/native";
import CustomHtml from "../components/common/general/CustomHtml/CustomHtml";
import Sentry from "../utils/sentry";
import RoundedLabel from "../components/common/label/RoundedLabel/RoundedLabel";
import { FileStatus, StoragePathType, useSubmitStudentAssignmentMutation } from "../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import { useAppState } from "../contexts/AppStateContext";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import CustomButton from "../components/common/general/CustomButton/CustomButton";
import NewCustomAxioActionModal from "../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import useFileUpload from "../hooks/useFileUpload";
import { shadowStyles } from "../styles/shadow";
import { useAppConfig } from "../AppConfigProvider";
import { File } from "../components/svgs/CustomCoursework";
import { Trash } from "@phosphor-icons/react/dist/csr/Trash";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";

const ResourceCard = ({ title, url, dateAdded, onPress, showDate = false, showDelete = false, onDelete = null }) => {
  const handleCardPress = async () => {
    if (!url) {
      return;
    }
    if (onPress) {
      onPress();
    } else if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else {
      try {
        await Linking.openURL(url);
      } catch (error) {
        console.error("Error opening URL:", error);
      }
    }
  };

  const formattedDate = dateAdded && showDate ? new Date(dateAdded).toLocaleDateString() : null;

  return (
    <MotiView
      from={{
        opacity: 0,
        translateX: -400,
      }}
      animate={{
        opacity: 1,
        translateX: 0,
      }}
      delay={200}
      // @ts-expect-error - transition is not typed
      transition={{
        type: "spring",
        damping: 18,
        stiffness: 120,
      }}>
      <Pressable onPress={handleCardPress} role="button" aria-label={`Open resource ${title}`}>
        <View style={styles.resourceCard}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <View style={{ flexDirection: "column" }}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <File width={16} height={16} style={{ paddingRight: 4 }} />
                <CustomText
                  useTranslationText={false}
                  numberOfLines={1}
                  text={title}
                  style={{ maxWidth: 200 }}
                  weight="normal"
                  size="s"
                />
              </View>
              {formattedDate && (
                <CustomText
                  useTranslationText={false}
                  text={`Added: ${formattedDate}`}
                  style={{ color: "#667085", marginTop: 4 }}
                  size="xs"
                />
              )}
            </View>
            {showDelete && onDelete && (
              <CustomButton styleType="transparent" onPress={onDelete} leftIcon={<Trash size={16} />} />
            )}
          </View>
        </View>
      </Pressable>
    </MotiView>
  );
};

// Grade display component
const GradeDisplay = ({ grade, feedback }) => {
  if (grade === null || grade === undefined) {
    return null;
  }

  return (
    <View style={styles.sectionContainer}>
      <CustomText text="Assignment Grade" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomSpacing type="vertical" size="m" />
      <View style={styles.gradeContainer}>
        <RoundedLabel
          text={`${grade}/100`}
          style={styles.gradedLabel}
          textStyle={styles.gradedLabelText}
          padding={[12, 8]}
          isRounded={true}
        />
      </View>

      {feedback && (
        <>
          <CustomSpacing type="vertical" size="m" />
          <CustomText text="Teacher Feedback:" size="s" weight="bold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.feedbackContainer}>
            <CustomText text={feedback} size="s" useTranslationText={false} style={styles.feedbackText} />
          </View>
        </>
      )}
    </View>
  );
};

function CustomAssignmentStudentView() {
  const route = useRoute<RouteProp<RootStackParamList, "Custom Assignment Student">>();
  const { resource_type: resourceType, resource_id: resourceId } = route.params;
  const appConfig = useAppConfig();
  const currentDomain = appConfig?.currentDomain || "";

  const [submitStudentAssignment] = useSubmitStudentAssignmentMutation();
  const [submissionConfirmationModalVisible, setSubmissionConfirmationModalVisible] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const { dispatch } = useAppState();

  const {
    assignmentData,
    loadingAssignment,
    setActiveResource,
    activeResource,
    refetchUserSubmissions,
    userSubmissionsData,
  } = useActiveCourse();

  useEffect(() => {
    setActiveResource({ resourceType: resourceType as ResourceType, resourceId });
  }, [resourceType, resourceId]);

  const [files, setFiles] = useState<
    Array<{
      id: string;
      fileName: string;
      fileURL: string;
      created: unknown;
      status: FileStatus;
    }>
  >([]);

  useEffect(() => {
    setFiles([]);
    refetchUserSubmissions();
  }, [refetchUserSubmissions, activeResource?.resourceId]);

  useEffect(() => {
    setFileUploadError(null);
  }, [activeResource?.resourceId]);

  useEffect(() => {
    if (userSubmissionsData?.getUserSubmissions?.data?.[0]?.files) {
      setFiles(userSubmissionsData?.getUserSubmissions?.data?.[0]?.files);
    }
  }, [userSubmissionsData]);

  // Helper functions for grade display
  const hasGrade = () => {
    // First check if we have a submission
    if (!userSubmissionsData?.getUserSubmissions?.data?.[0]) {
      return false;
    }

    // Then check if it has been graded
    const submission = userSubmissionsData.getUserSubmissions.data[0];
    return submission.hasTeacherGraded && submission.grade !== null && submission.grade !== undefined;
  };

  const getSubmissionGrade = () => {
    if (!hasGrade()) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data[0].grade;
  };

  const getSubmissionFeedback = () => {
    if (!hasGrade()) {
      return null;
    }
    return userSubmissionsData.getUserSubmissions.data[0].feedback;
  };

  useFocusEffect(
    useCallback(() => {
      dispatch({
        type: "SET_MEDIA",
        payload: {
          video: null,
          wiki: null,
        },
      });

      return () => {
        dispatch({
          type: "SET_MEDIA",
          payload: {
            video: null,
            wiki: null,
          },
        });
      };
    }, [dispatch])
  );

  const extractYoutubeVideoId = (url) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^[a-zA-Z0-9_-]{11}$/,
    ];

    for (const pattern of patterns) {
      const match = url?.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  const handleFileClick = async (file) => {
    try {
      handleOpenURL(file.fileURL);
    } catch (error) {
      console.error("Error handling file click:", error);
      handleOpenURL(file.fileURL);
    }
  };

  const handlePendingFileClick = (file) => {
    if (file.fileURL && Platform.OS === "web") {
      window.open(file.fileURL, "_blank");
    } else if (file.fileURL) {
      Linking.openURL(file.fileURL).catch((err) => {
        console.error("Error opening pending file URL:", err);
      });
    }
  };

  const handleDeleteFile = (fileId) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleBeforeUpload = () => {
    setPendingSubmission(true);
    setFileUploadError(null);
  };

  const handleAfterUpload = ({ id, fileName, fileURL }) => {
    setFiles((prev) => [...prev, { id, fileName, fileURL, created: new Date(), status: FileStatus.Pending }]);
    setPendingSubmission(false);
  };

  const handleFileUploadError = () => {
    setPendingSubmission(false);
    setFileUploadError(
      "Failed to upload file. Please ensure the file is less than 2MB and is a supported format (PDF, DOCX, XLSX, PPTX, XLS, DOC, CSV, TXT)."
    );
  };

  const { renderFileUpload } = useFileUpload({
    storagePath: StoragePathType.ResourceDocument,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
  });

  const handleSubmitAssignment = async () => {
    try {
      const pendingFiles = files.filter((file) => file.status === FileStatus.Pending);
      if (pendingFiles.length === 0) {
        return;
      }
      await submitStudentAssignment({
        variables: {
          assignmentId: activeResource?.resourceId,
          files: pendingFiles.map((file) => ({ fileId: file.id, fileName: file.fileName, fileURL: file.fileURL })),
        },
      });
      setSubmissionConfirmationModalVisible(true);
      setPendingSubmission(false);
    } catch (error) {
      console.error("Error submitting assignment:", error);
    }
  };

  const handleCloseSubmissionModal = () => {
    setSubmissionConfirmationModalVisible(false);
    refetchUserSubmissions();
  };

  const renderSubmittedAssignments = () => {
    // Filter to only show submitted files (not pending)
    const submittedFiles = files.filter((file) => file.status !== FileStatus.Pending);

    if (!submittedFiles || submittedFiles.length === 0) {
      return (
        <View style={styles.noSubmissionsContainer}>
          <CustomText
            text="Looks like you haven't submitted anything for this assignment."
            size="m"
            useTranslationText={false}
            style={{ justifyContent: "center", textAlign: "center" }}
          />
        </View>
      );
    }

    return (
      <View style={styles.filesContainer}>
        {submittedFiles.map((file) => (
          <ResourceCard
            key={file.id}
            title={file.fileName}
            url={file.fileURL}
            dateAdded={file.created}
            onPress={() => handleFileClick(file)}
            showDate={true}
          />
        ))}
      </View>
    );
  };

  const renderSkeletonLoader = () => (
    <View style={styles.skeletonContainer}>
      <View style={styles.detailsContainer}>
        <View style={styles.headerContainer}>
          <Skeleton width={40} height={40} circle={true} />
          <CustomSpacing type="horizontal" size="m" />
          <Skeleton width="60%" height={24} />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="30%" height={40} borderRadius={8} />
        <CustomSpacing type="vertical" size="l" />
        <Skeleton width="100%" height={200} borderRadius={12} />
      </View>
    </View>
  );

  const handleOpenURL = async (url) => {
    const fullUrl = url.startsWith("/") ? `https://${currentDomain}${url}` : url;

    if (Platform.OS === "web") {
      try {
        const token = await AsyncStorage.getItem("token");
        const form = document.createElement("form");
        form.method = "POST";
        form.action = fullUrl;
        form.target = "_blank";

        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "token";
        tokenInput.value = token;
        form.appendChild(tokenInput);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    } else {
      try {
        const token = await AsyncStorage.getItem("token");
        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `token=${encodeURIComponent(token)}`,
        });
        const finalUrl = response.url;
        await Linking.openURL(finalUrl);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    }
  };

  const AssignmentYoutubePlayer = ({ videoId }) => {
    return (
      <View style={styles.fullWidthVideoContainer}>
        <WebView
          style={styles.webView}
          source={{
            uri: `https://www.youtube.com/embed/${videoId}`,
          }}
          javaScriptEnabled={true}
          allowsFullscreenVideo={true}
        />
      </View>
    );
  };

  const renderMainContent = () => {
    if (loadingAssignment) {
      return renderSkeletonLoader();
    }

    return (
      <View style={styles.contentRow}>
        <View style={styles.leftColumn}>
          <CustomSpacing type="vertical" size="l" />

          {assignmentData?.getAssignment?.data?.[0]?.description && (
            <View style={styles.descriptionContainer}>
              <CustomText text="Instructions:" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomSpacing type="vertical" size="s" />
              <CustomHtml html={assignmentData.getAssignment.data[0].description} />
            </View>
          )}

          {assignmentData?.getAssignment?.data?.[0]?.videoUrl && (
            <View style={styles.sectionContainer}>
              <CustomText text="Video" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomSpacing type="vertical" size="s" />
              {extractYoutubeVideoId(assignmentData?.getAssignment?.data?.[0]?.videoUrl) ? (
                <AssignmentYoutubePlayer
                  videoId={extractYoutubeVideoId(assignmentData?.getAssignment?.data?.[0]?.videoUrl)}
                />
              ) : (
                <CustomText
                  text={assignmentData?.getAssignment?.data?.[0]?.videoUrl}
                  useTranslationText={false}
                  style={styles.linkText}
                  onPress={() => handleOpenURL(assignmentData?.getAssignment?.data?.[0]?.videoUrl)}
                />
              )}
            </View>
          )}
        </View>

        <View style={styles.rightColumn}>
          {hasGrade() ? (
            <GradeDisplay grade={getSubmissionGrade()} feedback={getSubmissionFeedback()} />
          ) : (
            <>
              {assignmentData?.getAssignment?.data?.[0]?.files?.length > 0 && (
                <View style={styles.sectionContainer}>
                  <CustomText text="Assignment Resources" size="m" weight="ultraBold" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="s" />
                  <View style={styles.filesContainer}>
                    {assignmentData.getAssignment.data[0].files.map((file) => (
                      <ResourceCard
                        key={file.id}
                        title={file.fileName}
                        url={file.fileURL}
                        dateAdded={file.created}
                        onPress={() => handleFileClick(file)}
                        showDate={false}
                      />
                    ))}
                  </View>
                </View>
              )}

              {assignmentData?.getAssignment?.data?.[0]?.enableStudentFileUpload && !hasGrade() && (
                <View style={styles.sectionContainer}>
                  <CustomText text="Upload Assignment" size="m" weight="ultraBold" useTranslationText={false} />
                  <CustomSpacing type="vertical" size="m" />

                  {!userSubmissionsData?.getUserSubmissions?.data?.[0]?.isCompleted ? (
                    <>
                      {renderFileUpload()}
                      {fileUploadError && (
                        <View style={{ marginTop: 8 }}>
                          <CustomText
                            text={fileUploadError}
                            size="xs"
                            style={{ color: "#E53E3E" }}
                            useTranslationText={false}
                          />
                        </View>
                      )}

                      <CustomSpacing type="vertical" size="m" />

                      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
                        <>
                          <View style={styles.filesContainer}>
                            {files
                              .filter((file) => file.status === FileStatus.Pending)
                              .map((file) => (
                                <ResourceCard
                                  key={file.id}
                                  title={file.fileName}
                                  url={file.fileURL}
                                  dateAdded={file.created}
                                  onPress={() => handlePendingFileClick(file)}
                                  showDate={false}
                                  showDelete={true}
                                  onDelete={() => handleDeleteFile(file.id)}
                                />
                              ))}
                          </View>
                          <CustomSpacing type="vertical" size="m" />
                        </>
                      )}

                      {/* Only show submit button if there are files */}
                      {files.length > 0 && files.some((file) => file.status === FileStatus.Pending) && (
                        <>
                          <CustomText
                            text="You can submit your assignment once. You cannot change your submission once submitted."
                            size="s"
                            style={styles.submissionInfoText}
                            useTranslationText={false}
                          />

                          <CustomSpacing type="vertical" size="m" />

                          <CustomButton
                            disabled={pendingSubmission}
                            text="Submit Assignment"
                            size="m"
                            onPress={handleSubmitAssignment}
                            style={pendingSubmission ? styles.disabledButton : styles.submitButton}
                            textStyle={pendingSubmission ? { color: "#475467" } : { color: "#FFFFFF" }}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <CustomText
                        text="You have already submitted this assignment. Your submission is being reviewed."
                        size="s"
                        style={styles.submissionInfoText}
                        useTranslationText={false}
                      />
                    </>
                  )}
                </View>
              )}

              <View style={styles.sectionContainer}>
                <CustomText text="Submitted Assignments" size="m" weight="ultraBold" useTranslationText={false} />
                <CustomSpacing type="vertical" size="m" />
                {renderSubmittedAssignments()}
              </View>
            </>
          )}
        </View>
      </View>
    );
  };

  return (
    <DefaultLayout showCourseNav={true} pageHasChat={false}>
      {renderMainContent()}

      <NewCustomAxioActionModal
        visible={submissionConfirmationModalVisible}
        onClose={handleCloseSubmissionModal}
        modalTexts={{
          title: "Submission Confirmation",
          warningText: "You have successfully submitted your assignment.",
          ariaLabel: "Submission Confirmation",
          actionButtonText: "Close",
        }}
      />
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  gradedLabel: {
    backgroundColor: "#ECFDF3",
    borderRadius: 360,
  },
  gradedLabelText: {
    color: "#027A48",
    fontSize: 12,
    fontWeight: "700",
  },
  contentRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  leftColumn: {
    width: "74%",
    paddingRight: 40,
    minWidth: 300,
  },
  rightColumn: {
    width: "25%",
    minWidth: 250,
  },
  descriptionContainer: {
    marginBottom: 20,
  },
  sectionContainer: {
    marginBottom: 30,
  },
  submissionInfoText: {
    color: "#6B7280",
  },
  submitButton: {
    backgroundColor: "#3E68FE",
    width: "100%",
  },
  disabledButton: {
    backgroundColor: "#EAECF0",
    width: "100%",
  },
  filesContainer: {
    marginBottom: 10,
    flexDirection: "column",
    gap: 10,
    width: "100%",
  },
  noSubmissionsContainer: {
    padding: 16,
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    marginBottom: 20,
  },
  linkText: {
    color: "#3B82F6",
    textDecorationLine: "underline",
  },
  skeletonContainer: {
    flex: 1,
    padding: 20,
  },
  detailsContainer: {
    flex: 1,
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  fullWidthVideoContainer: {
    width: "100%",
    aspectRatio: 16 / 9,
    marginBottom: 20,
    overflow: "hidden",
    borderRadius: 8,
    ...shadowStyles.smallShadow,
  },
  webView: {
    width: "100%",
    height: "100%",
  },
  resourceCard: {
    width: 290,
    flexDirection: "column",
    padding: 10,
    backgroundColor: "#fff",
    borderColor: "#D9D9D9",
    borderWidth: 1,
    borderRadius: 10,
    justifyContent: "flex-start",
    marginBottom: 12,
  },
  gradeContainer: {
    marginVertical: 8,
    alignItems: "flex-start",
  },
  feedbackContainer: {
    padding: 12,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  feedbackText: {
    color: "#374151",
    lineHeight: 20,
  },
});

export default CustomAssignmentStudentView;
